import { SnapshotLabelHtmlClassNames } from "@/animation/video-generator";
import type { EnergyPlot } from "@/energyPlotView/types";
import { ToolLoadingStateProvider } from "@/grid/ToolLoadingStateContext";
import { useAppDispatch } from "@/reducer";
import { addEnergyPlot } from "@/reducer/EnergyPlotsState";
import { classNames } from "@/utility/jsx";
import type { GridCellLayout } from "@mm/metx-workbench.meteomatics.com";
import { Suspense, lazy, memo, useEffect, useRef } from "react";

export type GridEnergyPlotProps = {
  energyPlot: EnergyPlot;
  cellLayout: GridCellLayout;
};

const EnergyPlotView = memo(
  lazy(() =>
    import("@/energyPlotView/EnergyPlotView")
      .then(({ EnergyPlotView }) => ({ default: EnergyPlotView }))
      .catch(() => {
        console.error("error");
        return { default: () => <div>Loading Energy Plot View failed</div> };
      }),
  ),
);
const EnergyPlotOverlay = memo(
  lazy(() =>
    import("@/overlay/overlays/default/EnergyPlotOverlay")
      .then(({ EnergyPlotOverlay }) => ({ default: EnergyPlotOverlay }))
      .catch(() => {
        console.error("error");
        return { default: () => <div>Loading Energy Plot View failed</div> };
      }),
  ),
);

export function GridEnergyPlot({ energyPlot, cellLayout }: GridEnergyPlotProps) {
  const dispatch = useAppDispatch();
  const registeredEnergyPlotId = useRef<number | null>(null);

  useEffect(() => {
    if (registeredEnergyPlotId.current === energyPlot.id) {
      return;
    }

    registeredEnergyPlotId.current = energyPlot.id;
    dispatch(addEnergyPlot(energyPlot));
  }, [dispatch, energyPlot]);

  if (!energyPlot) {
    return null;
  }

  return (
    <ToolLoadingStateProvider>
      <div className="overlay">
        <div className="overlay__layer">
          <Suspense fallback={<mm-loader size="large" />}>
            <EnergyPlotView energyPlot={energyPlot} />
          </Suspense>
        </div>
        <div className={classNames("overlay__layer", "gui-overlay", SnapshotLabelHtmlClassNames.EnergyPlot)}>
          <Suspense fallback={null}>
            <EnergyPlotOverlay plot={energyPlot} cellLayout={cellLayout} />
          </Suspense>
        </div>
      </div>
    </ToolLoadingStateProvider>
  );
}
